import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { ApiError, api, unwrap } from "./client";
import type { components } from "./schema.gen";

type S = components["schemas"];
export type SystemInfo = S["SystemInfo"];
export type Me = S["MeOut"];
export type AdminSession = S["AdminSessionOut"];
export type User = S["UserOut"];
export type UserCreate = S["UserCreateIn"];
export type UserUpdate = S["UserUpdateIn"];
export type RoleAssignment = S["RoleAssignmentIO"];
export type Project = S["ProjectOut"];
export type ProjectDetail = S["ProjectDetail"];
export type FormOut = S["FormOut"];
export type SchemeIO = S["SchemeIO"];
export type SchemeOut = S["SchemeOut"];
export type SchemeAnalysis = S["SchemeAnalysisOut"];
export type WorklistRow = S["WorklistRow"];
export type WorklistPage = S["WorklistPage"];
export type WorklistCounts = S["WorklistCounts"];
export type ScreeningDetail = S["ScreeningDetail"];
export type Patient = S["PatientOut"];
export type Screening = S["ScreeningOut"];
export type DuplicateCandidate = S["DuplicateCandidate"];
export type HistoryEntry = S["HistoryEntry"];
export type RegistrationIn = S["RegistrationIn"];
export type Imaging = S["ImagingOut"];
export type ImagingStudy = S["ImagingStudyOut"];
export type AiResult = S["AiResultOut"];
export type Lesion = S["LesionOut"];
export type ImagingOrder = S["OrderOut"];
export type Upload = S["UploadOut"];
export type UploadItem = S["UploadItemOut"];
export type Unmatched = S["UnmatchedOut"];
export type Integrations = S["IntegrationsOut"];
export type InferReadIn = S["InferReadIn"];
export type WorklistSettings = S["WorklistIO"];
export type IntegrationCheck = S["CheckOut"];
export type Clinical = S["ClinicalOut"];
export type Read = S["ReadOut"];
export type LabTest = S["LabOut"];
export type LabQueueRow = S["LabQueueRow"];
export type Outcome = S["OutcomeOut"];
export type ClinicalConfig = S["ClinicalConfigIO"];
export type ReportState = S["ReportStateOut"];
export type ReportSection = S["ReportSection"];
export type AuditPage = S["AuditPage"];
export type AuditEvent = S["AuditOut"];
export type SyncStatus = S["SyncStatus"];
export type Device = S["DeviceOut"];
export type DeviceCreated = S["DeviceCreatedOut"];
export type Enrollment = S["EnrollmentOut"];
export type Conflict = S["ConflictOut"];
export type Dashboard = S["DashboardOut"];
export type Kpis = S["Kpis"];
export type Thresholds = S["ThresholdsOut"];
export type ThresholdVersion = S["VersionOut"];
export type CalculateIn = S["CalculateIn"];
export type Calculation = S["CalculationOut"];
export type RocPoint = S["PointOut"];
export type ExportIn = S["ExportIn"];
export type Backups = S["BackupsOut"];
export type LogEntry = S["LogEntry"];
export type LabView = "open" | "requested" | "collected" | "in_progress" | "resulted_today";

export interface AuditFilters {
  action?: string;
  actor?: string;
  date_from?: string;
  date_to?: string;
  /** Time zone the dates are in (the project's); defaults to the browser's. */
  tz?: string;
}

export interface DashboardFilters {
  date_from?: string;
  date_to?: string;
  /** Cloud: where screenings were recorded ("cloud" or "device:<id>"). */
  origin?: string;
}

export interface WorklistFilters {
  date_from?: string;
  date_to?: string;
  symptom_screen?: "positive" | "negative" | "incomplete";
  tb_positive?: boolean;
  status?: string;
  q?: string;
}

export const keys = {
  system: ["system-info"] as const,
  me: ["me"] as const,
  adminSession: ["admin-session"] as const,
  users: (q?: string) => ["users", q ?? ""] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  form: (id: string) => ["project", id, "form"] as const,
  draft: (id: string) => ["project", id, "draft"] as const,
  scheme: (id: string) => ["project", id, "scheme"] as const,
  worklist: (id: string, f: WorklistFilters) => ["project", id, "worklist", f] as const,
  worklistPage: (id: string, f: WorklistFilters, cursor: string, limit: number) => ["project", id, "worklist", f, cursor, limit] as const,
  counts: (id: string, f: WorklistFilters) => ["project", id, "counts", f] as const,
  screening: (id: string) => ["screening", id] as const,
  history: (id: string) => ["screening", id, "history"] as const,
  imaging: (id: string) => ["screening", id, "imaging"] as const,
  unmatched: (id: string) => ["project", id, "unmatched"] as const,
  integrations: ["integrations"] as const,
  clinical: (id: string) => ["screening", id, "clinical"] as const,
  report: (id: string) => ["screening", id, "report"] as const,
  labs: (projectId: string, view: LabView) => ["project", projectId, "labs", view] as const,
  clinicalConfig: (projectId: string) => ["project", projectId, "clinical-config"] as const,
  audit: (f: AuditFilters) => ["audit", f] as const,
  syncStatus: ["admin-sync"] as const,
  devices: ["devices"] as const,
  conflicts: (status: "open" | "resolved") => ["conflicts", status] as const,
  dashboard: (id: string, f: DashboardFilters) => ["project", id, "dashboard", f] as const,
  thresholds: (id: string) => ["project", id, "thresholds"] as const,
  backups: ["admin-backups"] as const,
  logs: (level: string) => ["admin-logs", level] as const,
};

export const systemInfoQuery = queryOptions({
  queryKey: keys.system,
  queryFn: () => unwrap(api.GET("/api/v1/system/info")),
  staleTime: 60_000,
});

/** The signed-in cloud user, or ``null`` when signed out. */
export const meQuery = queryOptions({
  queryKey: keys.me,
  queryFn: async (): Promise<Me | null> => {
    try {
      return await unwrap(api.GET("/api/v1/auth/me"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  staleTime: 30_000,
});

export const adminSessionQuery = queryOptions({
  queryKey: keys.adminSession,
  queryFn: () => unwrap(api.GET("/api/v1/admin/session")),
});

export const usersQuery = (q?: string) =>
  queryOptions({
    queryKey: keys.users(q),
    queryFn: () => unwrap(api.GET("/api/v1/users", { params: { query: q ? { q } : {} } })),
  });

export const projectsQuery = queryOptions({
  queryKey: keys.projects,
  queryFn: () => unwrap(api.GET("/api/v1/projects")),
});

const path = (project_id: string) => ({ params: { path: { project_id } } });

export const projectQuery = (id: string) =>
  queryOptions({ queryKey: keys.project(id), queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}", path(id))) });

export const formQuery = (id: string) =>
  queryOptions({
    queryKey: keys.form(id),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/form", path(id))),
    staleTime: 60_000,
  });

export const draftQuery = (id: string) =>
  queryOptions({
    queryKey: keys.draft(id),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/form/draft", path(id))),
  });

export const schemeQuery = (id: string) =>
  queryOptions({
    queryKey: keys.scheme(id),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/patient-id-scheme", path(id))),
  });

/** ``id`` is a project, or ``"all"``: every project the user can read (the cloud's cross-project list). */
export const worklistQuery = (id: string, f: WorklistFilters) =>
  infiniteQueryOptions({
    queryKey: keys.worklist(id, f),
    queryFn: ({ pageParam }) => {
      const query = { ...f, limit: 50, ...(pageParam ? { cursor: pageParam } : {}) };
      return unwrap(
        id === "all"
          ? api.GET("/api/v1/worklist", { params: { query } })
          : api.GET("/api/v1/projects/{project_id}/screenings", { params: { path: { project_id: id }, query } }),
      );
    },
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** One page of the worklist, from ``cursor`` ("" for the first). Pages follow each other by cursor,
 *  so screenings registered meanwhile don't shift the rows of the pages after the first. */
export const worklistPageQuery = (id: string, f: WorklistFilters, cursor: string, limit: number) =>
  queryOptions({
    queryKey: keys.worklistPage(id, f, cursor, limit),
    queryFn: () => {
      const query = { ...f, limit, ...(cursor ? { cursor } : {}) };
      return unwrap(
        id === "all"
          ? api.GET("/api/v1/worklist", { params: { query } })
          : api.GET("/api/v1/projects/{project_id}/screenings", { params: { path: { project_id: id }, query } }),
      );
    },
  });

export const countsQuery = (id: string, f: WorklistFilters) =>
  queryOptions({
    queryKey: keys.counts(id, f),
    queryFn: () => {
      const query = { date_from: f.date_from, date_to: f.date_to, q: f.q };
      return unwrap(
        id === "all"
          ? api.GET("/api/v1/worklist/counts", { params: { query } })
          : api.GET("/api/v1/projects/{project_id}/screenings/counts", { params: { path: { project_id: id }, query } }),
      );
    },
  });

export const syncStatusQuery = queryOptions({
  queryKey: keys.syncStatus,
  queryFn: () => unwrap(api.GET("/api/v1/admin/sync")),
  refetchInterval: 10_000,
});

export const devicesQuery = queryOptions({
  queryKey: keys.devices,
  queryFn: () => unwrap(api.GET("/api/v1/devices")),
  refetchInterval: 30_000,
});

export const conflictsQuery = (status: "open" | "resolved") =>
  queryOptions({
    queryKey: keys.conflicts(status),
    queryFn: () => unwrap(api.GET("/api/v1/sync/conflicts", { params: { query: { status } } })),
  });

export const screeningQuery = (id: string) =>
  queryOptions({
    queryKey: keys.screening(id),
    queryFn: () => unwrap(api.GET("/api/v1/screenings/{screening_id}", { params: { path: { screening_id: id } } })),
  });

export const historyQuery = (id: string) =>
  queryOptions({
    queryKey: keys.history(id),
    queryFn: () =>
      unwrap(api.GET("/api/v1/screenings/{screening_id}/history", { params: { path: { screening_id: id } } })),
  });

export const imagingQuery = (id: string) =>
  queryOptions({
    queryKey: keys.imaging(id),
    queryFn: () =>
      unwrap(api.GET("/api/v1/screenings/{screening_id}/imaging", { params: { path: { screening_id: id } } })),
  });

export const unmatchedQuery = (projectId: string) =>
  queryOptions({
    queryKey: keys.unmatched(projectId),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/unmatched-images", path(projectId))),
  });

export const integrationsQuery = queryOptions({
  queryKey: keys.integrations,
  queryFn: () => unwrap(api.GET("/api/v1/integrations")),
});

export const clinicalQuery = (id: string) =>
  queryOptions({
    queryKey: keys.clinical(id),
    queryFn: () =>
      unwrap(api.GET("/api/v1/screenings/{screening_id}/clinical", { params: { path: { screening_id: id } } })),
  });

export const reportQuery = (id: string) =>
  queryOptions({
    queryKey: keys.report(id),
    queryFn: () =>
      unwrap(api.GET("/api/v1/screenings/{screening_id}/report", { params: { path: { screening_id: id } } })),
  });

export const labQueueQuery = (projectId: string, view: LabView) =>
  queryOptions({
    queryKey: keys.labs(projectId, view),
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/projects/{project_id}/labs", { params: { path: { project_id: projectId }, query: { view } } }),
      ),
  });

export const clinicalConfigQuery = (projectId: string) =>
  queryOptions({
    queryKey: keys.clinicalConfig(projectId),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/clinical-config", path(projectId))),
  });

export const auditQuery = (f: AuditFilters) =>
  infiniteQueryOptions({
    queryKey: keys.audit(f),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/api/v1/audit", {
          params: {
            query: {
              ...f,
              tz: f.tz ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
              limit: 50,
              ...(pageParam ? { cursor: pageParam } : {}),
            },
          },
        }),
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

/** ``id`` is a project, or ``"all"``: every project the user can read. */
export const dashboardQuery = (id: string, f: DashboardFilters) =>
  queryOptions({
    queryKey: keys.dashboard(id, f),
    queryFn: () =>
      unwrap(
        id === "all"
          ? api.GET("/api/v1/dashboard", { params: { query: f } })
          : api.GET("/api/v1/projects/{project_id}/dashboard", { params: { path: { project_id: id }, query: f } }),
      ),
    refetchInterval: 60_000,
  });

export const thresholdsQuery = (projectId: string) =>
  queryOptions({
    queryKey: keys.thresholds(projectId),
    queryFn: () => unwrap(api.GET("/api/v1/projects/{project_id}/thresholds", path(projectId))),
    refetchInterval: 30_000,
  });

export const backupsQuery = queryOptions({
  queryKey: keys.backups,
  queryFn: () => unwrap(api.GET("/api/v1/admin/backups")),
  // Quick while a backup is being made, then every half minute.
  refetchInterval: (q) => (q.state.data?.status.busy || q.state.data?.status.requested ? 3_000 : 30_000),
});

export const logsQuery = (level: "info" | "warning") =>
  queryOptions({
    queryKey: keys.logs(level),
    queryFn: () => unwrap(api.GET("/api/v1/admin/logs", { params: { query: { level } } })),
    refetchInterval: 15_000,
  });
